import React from 'react';
import { RouteComponentProps } from '@reach/router';
import { useGetUserQuery, UserStatus } from '~/graphql/types';

import useCurrentAccount from '~/hooks/useCurrentAccount';
import { Loading, AppErrorScreen } from '~/components';
import {
  getOfficesList,
  OfficeInList,
} from '~/scenes/Settings/Profile/components/FormContainer';
import UserDetailsComponent from './UserDetailsComponent';
import { isInitialLoadStatus } from '~/graphql/ApolloConstants';

type Props = RouteComponentProps<{ userId: string }>;

const UserDetails: React.FC<Props> = ({ userId = '' }: Props) => {
  const account = useCurrentAccount();

  const { data, networkStatus, refetch, startPolling, stopPolling } =
    useGetUserQuery({
      variables: {
        accountId: account.id,
        userId,
      },
    });

  if (isInitialLoadStatus(networkStatus)) return <Loading />;
  if (!data) return <AppErrorScreen />;

  const user = data.getUser;
  if (!user) return <AppErrorScreen />;

  const { email, phone } = user;

  const reducedOffices = getOfficesList(user.Offices);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { officeIds, ...initialOfficeData } = reducedOffices;

  const initialValue = {
    email,
    phone,
    ...initialOfficeData,
  };

  const userIsBeingDeletedFromOffice = user.Offices.some(
    office => office.relation?.status === UserStatus.Deleting,
  );

  const signature = user.Signature ?? null;

  return (
    <UserDetailsComponent
      user={{ name: user.name, id: user.id, img: user.img }}
      officesList={user.Offices.map(convertToOfficeInList)}
      initialValue={initialValue}
      userIsBeingDeletedFromOffice={userIsBeingDeletedFromOffice}
      refetch={refetch}
      startPolling={startPolling}
      stopPolling={stopPolling}
      signature={signature}
      notificationSettings={data?.getUser?.NotificationSettings}
    />
  );
};

const convertToOfficeInList = (office): OfficeInList => {
  const { address, email, id, name, phone, img, relation } = office;

  return {
    address,
    email,
    id,
    name,
    phone,
    img,
    relation,
  };
};

export default UserDetails;
